import {
  sourceTierFromContext,
  type ProductClass,
  type SizeTier,
  matchProduct,
  type MatchProduct,
  type PositionRequirement,
  type ProductMatchResult,
  type ProductMismatch,
} from '@designdeck/shared';
import i18n from '@/lib/i18n';
import type { UIProduct } from '@/lib/product-transformer';
import type { ProductsSort, ProductsStatusFilter } from '@/stores/uiStore';

/**
 * The step-2 catalogue as pure data: which products are shown, in what order,
 * and why a product does not carry the design.
 *
 * Kept out of the component so the filter combinations — the part that is
 * easy to get subtly wrong — are tested on their own.
 */

export type CatalogSort = ProductsSort;

export interface CatalogFilterState {
  category: string;
  status: ProductsStatusFilter;
  manufacturerIds: string[];
  onlyMatching: boolean;
  sort: CatalogSort;
  /** The price the rendered card shows; defaults to `blankCost`. */
  priceKey?: CatalogPriceKey;
}

/**
 * Which product price a price sort orders by: the guided catalogue card shows
 * `startingCost`, the classic product card shows `blankCost`.
 */
export type CatalogPriceKey = 'startingCost' | 'blankCost';

export const DEFAULT_CATALOG_FILTERS: CatalogFilterState = {
  category: 'all',
  status: 'all',
  manufacturerIds: [],
  onlyMatching: false,
  sort: 'default',
};

/** Whether any filter departs from the defaults (sort is not a filter). */
export function hasActiveCatalogFilters(state: CatalogFilterState): boolean {
  return (
    state.category !== DEFAULT_CATALOG_FILTERS.category ||
    state.status !== DEFAULT_CATALOG_FILTERS.status ||
    state.manufacturerIds.length > 0 ||
    state.onlyMatching
  );
}

/**
 * Minimal view of a stored position, as canvasStateManager keeps it.
 * Declared here so the requirement builder can be tested without the store.
 */
export interface StoredPositionLike {
  json: { objects?: unknown[] } | null;
  relativeState: { relWidth: number } | null;
  printContext: {
    widthPx: number;
    widthMm: number;
    pxPerMm: number;
    sizeTier?: SizeTier;
    /** The reference product the context was measured on, when known. */
    productId?: string | null;
    productClass?: ProductClass | null;
  } | null;
}

/**
 * An embroidery motif carries the storage key of the image it was cut from —
 * the same marker the publish rules key on, so both read the same answer.
 */
export function isEmbroideredPosition(position: StoredPositionLike): boolean {
  return (position.json?.objects ?? []).some(
    (object) =>
      typeof (object as { embFileKey?: unknown })?.embFileKey === 'string'
  );
}

/**
 * What the design needs per Position, from the stored master positions.
 *
 * The width is the design's relative width on the reference Print Area turned
 * into millimetres. A context without a physical width leaves the width at 0,
 * which the predicate reads as "check the Position, not the size". A Position
 * holding an embroidery motif needs a Position that can be embroidered.
 */
export function requirementsFromPositions(
  positions: Record<string, StoredPositionLike | undefined>
): PositionRequirement[] {
  const requirements: PositionRequirement[] = [];
  for (const [slug, position] of Object.entries(positions)) {
    if (!position?.json?.objects?.length) continue;
    const rel = position.relativeState?.relWidth ?? 0;
    const ctx = position.printContext;
    let widthMm = 0;
    if (ctx && rel > 0) {
      if (ctx.widthMm > 0) widthMm = rel * ctx.widthMm;
      else if (ctx.pxPerMm > 0) widthMm = (rel * ctx.widthPx) / ctx.pxPerMm;
    }
    // The reference area's tier: onto a KLEIN product the width check does
    // not apply, because the carry-over maximises the design there.
    const sourceSizeTier = ctx
      ? sourceTierFromContext({
          sizeTier: ctx.sizeTier,
          widthMm: ctx.widthMm,
          printWidthPx: ctx.widthPx,
          pxPerMm: ctx.pxPerMm,
        })
      : undefined;
    // The reference product and its class: the carry-over rule reads them to
    // tell a product-dependent fit (percent, class change) from the relative
    // rule, the only one the width check applies to.
    const sourceProductId = ctx?.productId ?? undefined;
    const sourceProductClass = ctx?.productClass ?? undefined;
    requirements.push({
      slug,
      widthMm,
      ...(sourceSizeTier !== undefined && { sourceSizeTier }),
      ...(sourceProductId !== undefined && { sourceProductId }),
      ...(sourceProductClass !== undefined && { sourceProductClass }),
      decoration: isEmbroideredPosition(position) ? 'embroidery' : 'print',
    });
  }
  return requirements;
}

/** Adapt a catalogue product to the shared predicate's shape. */
export function toMatchProduct(product: UIProduct): MatchProduct {
  return {
    productId: product.id,
    productClass: product.productClass ?? null,
    positions: product.matchPositions,
  };
}

export interface CatalogEntry {
  product: UIProduct;
  match: ProductMatchResult;
}

export interface CatalogResult {
  /** Products to render, in display order. */
  shown: CatalogEntry[];
  /** How many products the catalogue holds before any filter. */
  total: number;
  /** How many were hidden by "only matching" alone. */
  hiddenByMatch: number;
}

function comparePrice(
  a: UIProduct,
  b: UIProduct,
  direction: 1 | -1,
  priceKey: CatalogPriceKey
): number {
  // Sorts by the price the card shows. A missing one sorts last in either
  // direction rather than pretending to be the cheapest product.
  const pa = a[priceKey];
  const pb = b[priceKey];
  if (pa === null && pb === null) return 0;
  if (pa === null) return 1;
  if (pb === null) return -1;
  return (pa - pb) * direction;
}

export function sortCatalog(
  entries: CatalogEntry[],
  sort: CatalogSort,
  priceKey: CatalogPriceKey = 'blankCost'
): CatalogEntry[] {
  if (sort === 'default') return entries;
  const sorted = [...entries];
  switch (sort) {
    case 'price-asc':
      sorted.sort((a, b) => comparePrice(a.product, b.product, 1, priceKey));
      break;
    case 'price-desc':
      sorted.sort((a, b) => comparePrice(a.product, b.product, -1, priceKey));
      break;
    case 'name':
      sorted.sort((a, b) =>
        a.product.name.localeCompare(b.product.name, 'de', {
          sensitivity: 'base',
        })
      );
      break;
  }
  return sorted;
}

/**
 * Apply every filter, then the sort.
 *
 * Category, status and manufacturer combine as AND; two manufacturers are the
 * union of both. "Only matching" removes products that do not carry the
 * design; with it off they stay, and the caller dims them with the reason.
 */
export function filterCatalog(
  products: UIProduct[],
  state: CatalogFilterState,
  requirements: PositionRequirement[]
): CatalogResult {
  let entries: CatalogEntry[] = products.map((product) => ({
    product,
    match: matchProduct(toMatchProduct(product), requirements),
  }));

  if (state.category !== 'all') {
    entries = entries.filter((e) => e.product.category === state.category);
  }
  if (state.status === 'active') {
    entries = entries.filter((e) => e.product.isActive);
  } else if (state.status === 'inactive') {
    entries = entries.filter((e) => !e.product.isActive);
  }
  if (state.manufacturerIds.length > 0) {
    const wanted = new Set(state.manufacturerIds);
    entries = entries.filter(
      (e) =>
        e.product.manufacturerId !== undefined &&
        wanted.has(e.product.manufacturerId)
    );
  }

  let hiddenByMatch = 0;
  if (state.onlyMatching) {
    const before = entries.length;
    entries = entries.filter((e) => e.match.matches);
    hiddenByMatch = before - entries.length;
  }

  return {
    shown: sortCatalog(entries, state.sort, state.priceKey),
    total: products.length,
    hiddenByMatch,
  };
}

/** Locale key per Position slug; an unknown slug is shown as is. */
const SLUG_LABEL_KEYS: Record<string, string> = {
  front: 'creator.catalog.positions.front',
  back: 'creator.catalog.positions.back',
  'back-inside': 'creator.catalog.positions.backInside',
  'left-sleeve': 'creator.catalog.positions.sleeveLeft',
  'right-sleeve': 'creator.catalog.positions.sleeveRight',
};

export function positionLabel(slug: string): string {
  const key = SLUG_LABEL_KEYS[slug];
  return key ? i18n.t(key) : slug;
}

/**
 * The mismatch that must block selecting the product, if any.
 *
 * A missing Position blocks: the motif for that side would be dropped silently
 * on save. A Position that cannot be decorated the way the motif was made
 * blocks too: the server refuses to publish an embroidery position without a
 * mapped motif, and a stitch render on a print position is a wrong file — the
 * pick would fail two steps later with no way to fix it there. A narrow Print
 * Area is shown on the card and left to the seller's judgement.
 */
export function blockingMismatch(
  match: ProductMatchResult
): ProductMismatch | null {
  return (
    match.mismatches.find(
      (m) =>
        m.reason === 'missing-position' ||
        m.reason === 'not-print' ||
        m.reason === 'not-embroidery'
    ) ?? null
  );
}

/** One short line saying why a product does not carry the design. */
export function mismatchLabel(match: ProductMatchResult): string | null {
  const first = match.mismatches[0];
  if (!first) return null;
  const position = positionLabel(first.slug);
  switch (first.reason) {
    case 'missing-position':
      return i18n.t('creator.catalog.mismatch.missingPosition', { position });
    case 'not-print':
      return i18n.t('creator.catalog.mismatch.notPrint', { position });
    case 'not-embroidery':
      return i18n.t('creator.catalog.mismatch.notEmbroidery', { position });
    case 'too-narrow':
      return i18n.t('creator.catalog.mismatch.tooNarrow', {
        position,
        max: Math.round((first.maxWidthMm ?? 0) / 10),
        width: Math.round((first.requiredWidthMm ?? 0) / 10),
      });
  }
}
